import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import { eur, packTotal } from "../../lib/format";
import { X, ShieldCheck, Truck, Check } from "lucide-react";

export const CartDrawer = () => {
  const { config, cartOpen, setCartOpen, selectedPack, selectPack } = useStore();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!config) return null;

  const handleCheckout = () => {
    setLoading(true);
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full sm:max-w-md bg-white shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-100">
              <h2 className="font-display font-bold text-lg">Your order</h2>
              <button data-testid="cart-close-button" onClick={() => setCartOpen(false)} aria-label="Close" className="p-2 -mr-2 text-zinc-400 hover:text-ink">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-3">
                {config.packs.map((pack) => {
                  const selected = selectedPack?.id === pack.id;
                  return (
                    <button
                      key={pack.id}
                      data-testid={`cart-pack-${pack.units}`}
                      onClick={() => selectPack(pack.id)}
                      className={`w-full flex items-center gap-4 rounded-2xl p-4 text-left transition-all ${
                        selected ? "ring-2 ring-brand bg-brand-soft/50" : "ring-1 ring-zinc-200 hover:ring-zinc-300"
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? "border-brand bg-brand" : "border-zinc-300"}`}>
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span className="flex-1">
                        <span className="block font-display font-semibold text-sm">{pack.name}</span>
                        {pack.badge && <span className="text-[10px] font-bold text-brand">{pack.badge}</span>}
                      </span>
                      <span className="font-display font-bold">{eur(pack.price)}</span>
                    </button>
                  );
                })}
              </div>
              {selectedPack && (
                <div className="mt-8 space-y-2.5 text-sm" data-testid="cart-summary">
                  <div className="flex justify-between text-zinc-500">
                    <span>{selectedPack.name}</span>
                    <span>{eur(selectedPack.price)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500">
                    <span className="flex items-center gap-1.5"><Truck className="w-4 h-4" /> Shipping</span>
                    <span className={selectedPack.free_shipping ? "text-green-600 font-semibold" : ""}>
                      {selectedPack.free_shipping ? "FREE" : eur(selectedPack.shipping)}
                    </span>
                  </div>
                  <div className="flex justify-between font-display font-bold text-lg pt-3 border-t border-zinc-100">
                    <span>Total</span>
                    <span data-testid="cart-total">{eur(packTotal(selectedPack))}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-5 border-t border-zinc-100">
              <motion.button
                data-testid="cart-checkout-button"
                onClick={handleCheckout}
                disabled={!selectedPack}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-display font-bold text-base rounded-full py-4 shadow-lg shadow-brand/30 transition-colors"
              >
                GO TO SECURE CHECKOUT
              </motion.button>
              <p className="mt-3 text-center text-xs text-zinc-400 flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Secure payment with Stripe · Card · Apple Pay · Google Pay
              </p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
